import ModalAsientos from '../operaciones/ModalAsientos';
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../servicios/firebase';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import BotonVolver from '../../components/ui/BotonVolver';
import ModalManifiesto from '../operaciones/ModalManifiesto';

const Salidas = () => {
  const { user } = useAuth();
  const location = useLocation();

  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [datosUsuario, setDatosUsuario] = useState(null);

  const [salidasDelDia, setSalidasDelDia] = useState([]);
  const [viajeParaVender, setViajeParaVender] = useState(null);
  const [viajeParaManifiesto, setViajeParaManifiesto] = useState(null);
  const [mapaZonas, setMapaZonas] = useState({});
  const [zonasDisponibles, setZonasDisponibles] = useState([]);

  // ── NUEVO: asientos a resaltar cuando viene desde notificación ────────────
  const [asientosResaltar, setAsientosResaltar] = useState([]);

  // ========== CARGAR DATOS DEL USUARIO ==========
  useEffect(() => {
    const cargarUsuario = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'usuarios', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setDatosUsuario(userData);
          console.log("👤 Usuario cargado:", userData.rol, userData.sucursal || "TODAS");
        }
      } catch (error) {
        console.error("Error cargando usuario:", error);
      }
    };
    cargarUsuario();
  }, [user]);

  useEffect(() => {
    const cargarTodo = async () => {
      if (!datosUsuario) return;

      setLoading(true);
      setSalidasDelDia([]);

      try {
        let zonasTemp = {};
        let listaNombresZonas = [];

        const termSnap = await getDoc(doc(db, 'configuraciones', 'terminales'));
        if (termSnap.exists()) {
          const data = termSnap.data();
          Object.entries(data).forEach(([zonaNombre, listaTerminales]) => {
            if (Array.isArray(listaTerminales)) {
              listaNombresZonas.push(zonaNombre);
              listaTerminales.forEach(terminalNombre => {
                zonasTemp[terminalNombre] = zonaNombre;
              });
            }
          });
          setMapaZonas(zonasTemp);
          setZonasDisponibles(listaNombresZonas);
        }

        const rutasSnap = await getDocs(collection(db, 'rutas'));
        let todasLasRutas = rutasSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.activo);

        if (datosUsuario.rol === 'admin_sucursal') {
          todasLasRutas = todasLasRutas.filter(r => r.origen === datosUsuario.sucursal);
          console.log(`🔒 Filtrado para ${datosUsuario.sucursal}: ${todasLasRutas.length} rutas`);
        } else {
          console.log(`🌍 Admin global ve ${todasLasRutas.length} rutas`);
        }

        const qViajes = query(collection(db, 'viajes'), where("fecha", "==", fechaSeleccionada));
        const viajesSnap = await getDocs(qViajes);
        let viajesReales = viajesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (datosUsuario.rol === 'admin_sucursal') {
          const rutasIdsPermitidos = todasLasRutas.map(r => r.id);
          viajesReales = viajesReales.filter(v => rutasIdsPermitidos.includes(v.rutaId));
        }

        let mix = [];
        const diaSemana = new Date(fechaSeleccionada + "T12:00:00").getDay();

        todasLasRutas.forEach(ruta => {
          if (ruta.diasOperacion && !ruta.diasOperacion.includes(diaSemana)) return;
          if (ruta.horariosSalida) {
            ruta.horariosSalida.forEach(hora => {
              const viajeExistente = viajesReales.find(v => v.hora === hora && v.rutaId === ruta.id);
              let salida = {
                tipo: viajeExistente ? 'REAL' : 'VIRTUAL',
                id: viajeExistente ? viajeExistente.id : null,
                idReferencia: `${fechaSeleccionada}_${hora}_${ruta.id}`,
                rutaId: ruta.id,
                fecha: fechaSeleccionada,
                hora: hora,
                origen: ruta.origen,
                destino: ruta.destino,
                precio: viajeExistente ? viajeExistente.precio : (ruta.precio || 350),
                ocupados: viajeExistente ? (viajeExistente.ocupados || []) : [],
                bloqueados: viajeExistente ? (viajeExistente.bloqueados || []) : [],
                zona: zonasTemp[ruta.origen] || 'General'
              };
              mix.push(salida);
            });
          }
        });

        mix.sort((a, b) => a.hora.localeCompare(b.hora));
        setSalidasDelDia(mix);

      } catch (error) {
        console.error("❌ Error cargando taquilla:", error);
      } finally {
        setLoading(false);
      }
    };

    cargarTodo();
  }, [fechaSeleccionada, datosUsuario]);

  // ── NUEVO: detectar params de URL (viene desde notificación) ─────────────
  // Cuando Salidas termina de cargar, busca el viaje indicado y abre el modal
  useEffect(() => {
    if (loading || salidasDelDia.length === 0) return;

    const params = new URLSearchParams(location.search);
    const viajeIdParam = params.get('viajeId');
    const asientosParam = params.get('asientos');
    const fechaParam = params.get('fecha');

    if (!viajeIdParam) return;

    // Si la fecha del param es diferente a la seleccionada, cambiarla primero
    // El useEffect de carga se volverá a ejecutar con la nueva fecha
    if (fechaParam && fechaParam !== fechaSeleccionada) {
      setFechaSeleccionada(fechaParam);
      return;
    }

    // Buscar el viaje en la lista ya cargada
    const viajeEncontrado = salidasDelDia.find(s =>
      s.id === viajeIdParam || s.idReferencia === viajeIdParam
    );

    if (viajeEncontrado) {
      const asientos = asientosParam ? asientosParam.split(',').filter(Boolean) : [];
      setAsientosResaltar(asientos);
      setViajeParaVender(viajeEncontrado);
      // Limpiar la URL para que no se repita si el usuario navega de nuevo
      window.history.replaceState({}, '', '/admin/salidas');
    }
  }, [loading, salidasDelDia, location.search]);

  // --- RENDERIZADO DE TARJETAS ---
  const renderTarjeta = (viaje) => {
    const totalOcupados = (viaje.ocupados?.length || 0) + (viaje.bloqueados?.length || 0);
    const libres = 16 - totalOcupados;
    const pct = (totalOcupados / 16) * 100;

    let color = libres === 0 ? 'bg-red-100 text-red-700' :
      libres < 5 ? 'bg-orange-100 text-orange-700' :
        'bg-green-100 text-green-700';

    return (
      <div key={viaje.idReferencia}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <span className="text-xl font-bold text-gray-800">{viaje.hora}</span>
          <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase ${color}`}>
            {libres} Libres
          </span>
        </div>
        <div className="p-4">
          <div className="text-sm font-bold text-gray-800 truncate" title={viaje.origen}>
            {viaje.origen}
          </div>
          <div className="text-xs text-gray-400 mb-2">⬇ {viaje.destino}</div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4">
            <div className={`h-1.5 rounded-full ${libres < 5 ? 'bg-orange-500' : 'bg-blue-600'}`}
              style={{ width: `${pct}%` }}>
            </div>
          </div>

          <button
            onClick={() => setViajeParaVender(viaje)}
            className="w-full py-2 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-black transition mb-2">
            🎟️ Vender
          </button>

          <button
            onClick={() => setViajeParaManifiesto(viaje)}
            className="w-full py-2.5 rounded-lg bg-blue-50 text-gray-600 border border-blue-200 text-xs font-bold hover:bg-blue-300 transition flex items-center justify-center gap-1 shadow-sm"
            title="Imprimir o Enviar Lista de Abordaje">
            📄 Lista de pasajeros
          </button>
        </div>
      </div>
    );
  };

  if (!datosUsuario) {
    return (
      <div className="p-6 max-w-7xl mx-auto min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔄</div>
          <p className="text-gray-500">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen bg-gray-50">

      <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">
            Taquilla {datosUsuario.rol === 'admin_sucursal' && `- ${datosUsuario.sucursal}`}
          </h1>
          <p className="text-gray-500 text-sm">
            Vista completa del día: {fechaSeleccionada}
            {datosUsuario.rol === 'admin_sucursal' &&
              <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">
                Solo tu sucursal
              </span>
            }
          </p>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="date"
            className="p-3 border rounded-xl font-bold text-gray-700"
            value={fechaSeleccionada}
            onChange={(e) => setFechaSeleccionada(e.target.value)}
          />
          <BotonVolver />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400">Cargando salidas...</div>
      ) : (
        <div className="space-y-8">
          {zonasDisponibles.length > 0 ? (
            <>
              {zonasDisponibles.map(zonaNombre => {
                const salidasZona = salidasDelDia.filter(s => s.zona === zonaNombre);
                if (salidasZona.length === 0) return null;
                return (
                  <div key={zonaNombre}>
                    <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2 border-b pb-2">
                      📍 {zonaNombre}
                      <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                        {salidasZona.length}
                      </span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {salidasZona.map(renderTarjeta)}
                    </div>
                  </div>
                );
              })}

              {salidasDelDia.filter(s => s.zona === 'General' || !s.zona).length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-gray-700 mb-4">Otras Salidas</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {salidasDelDia.filter(s => s.zona === 'General' || !s.zona).map(renderTarjeta)}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {salidasDelDia.map(renderTarjeta)}
            </div>
          )}

          {salidasDelDia.length === 0 && (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
              <div className="text-4xl mb-2">😴</div>
              <h3 className="text-lg font-bold text-gray-600">No hay salidas para esta fecha</h3>
              <p className="text-sm text-gray-400">
                {datosUsuario.rol === 'admin_sucursal'
                  ? `No hay rutas configuradas para ${datosUsuario.sucursal} en este día.`
                  : 'Revisa que existan rutas activas para el día de la semana correspondiente.'
                }
              </p>
            </div>
          )}
        </div>
      )}

      {viajeParaVender && (
        <ModalAsientos
          viaje={viajeParaVender}
          asientosResaltar={asientosResaltar}
          onClose={() => { setViajeParaVender(null); setAsientosResaltar([]); }}
          onVentaExitosa={() => {
            const current = fechaSeleccionada;
            setFechaSeleccionada("");
            setTimeout(() => setFechaSeleccionada(current), 100);
          }}
        />
      )}

      {viajeParaManifiesto && (
        <ModalManifiesto
          viaje={viajeParaManifiesto}
          onClose={() => setViajeParaManifiesto(null)}
        />
      )}
    </div>
  );
};

export default Salidas;